import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { fmtLocalDate } from '../../../utils/datetime';
import { fetchRequisitionTracking, type RequisitionTrackingRow } from './icmTrackingApi';
import '../../backlog/backlog.css';
import '../alerts/icmAlerts.css';

/**
 * Inventory Management → Tracking → Item Requisition Tracking.
 *
 * Requisitions currently Pending Review — "what's waiting on a reviewer".
 * Ported from ALPACA `Requisition_ListView_Tracking` (criteria
 * `[Status] = PendingReview`), which is read-only: AllowNew / AllowEdit /
 * AllowDelete / AllowUnlink are all False and the Edit action link is hidden.
 * So this form has Refresh and Export only. Legacy sets `ShowSelectionColumn=
 * False` on this view, but a row-selection checkbox column is shown here on
 * user request (2026-09-22) for consistency with the other Tracking grids.
 */
export default function ItemRequisitionTrackingPage() {
  const [rows, setRows] = useState<RequisitionTrackingRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const page = await fetchRequisitionTracking();
      setRows(page.rows);
      setTotal(page.total);
      setSelected(new Set());
    } catch {
      setError('Failed to load item requisition tracking.');
      setRows([]); setTotal(0);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { void load(); }, [load]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.rqid ?? '').toLowerCase().includes(t) ||
      (r.itemName ?? '').toLowerCase().includes(t) ||
      (r.itemCode ?? '').toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.requestedByName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /* Column set + order from the LIVE GoLive grid (owner screenshot 2026-10-06):
     Date · Item · Description · Vendor · OrderQty · StockQty · PackUnits ·
     DeliveryPriority · Department · RequestedBy · Status.

     ⚠ THE LIVE GRID AND THE SOURCE MODEL DISAGREE, and the live grid wins --
     the same ruling as Workflow Status Chain (2026-09-12). The source copy of
     Requisition_ListView_Tracking (BTLIMS.Web/Model.xafml :41038) also carries
     `Catalog` at Index 3 and `Division` at Index 1, and GoLive renders neither.
     Division is dropped at runtime by DivisionBasedFilterViewController (:86,
     which targets this view by name); Catalog is simply not there, and
     ListViewColumnWidthController's own block for this view (:5447) has its
     `Columns["Catalog#"]` width line COMMENTED OUT while keeping live lines for
     ItemName / Description / DeliveryPriority -- consistent with a column that
     is not rendered.

     ⚠ RQ# IS HIDDEN IN THE MODEL TOO: `ColumnInfo Id="RQID" Index="-1"`. Both
     sources agree on that one; AI had simply added it.

     "Description" IS `Item.Specification` -- the model's ColumnInfo carries no
     Caption, so XAF falls back to the property's own
     `[XafDisplayName("Description")]` (References/Modules/BusinessObjects/ICM/
     Items.cs :178). Same data, legacy's label; AI was showing the raw property
     name.

     StockQty is legacy's division-aware Items.DivisionStock (same figure as
     Stock Watch), computed in the SP -- not the raw Items.StockQty (2026-09-29).

     `rqid` and `catalog` stay on the ROW TYPE: both are still searched
     server-side, which is why you can still find a row by its RQ#. */
  const cols: AppColumn<RequisitionTrackingRow>[] = [
    /* DATE ONLY. The legacy column carries an explicit
       DisplayFormat="{0:d}" (Requisition_ListView_Tracking, the Date column), so
       GoLive renders the date and never the time. AI used fmtLocalDateTime,
       which also meant the 1,339 of 1,476 requisitions stored at exactly
       midnight rendered as "04/20/2026 00:00" -- a time nobody recorded, shown
       as though it were. The other four requisition grids already use
       fmtLocalDate. */
    { field: 'requestedDate', header: 'Date',        width: 110, type: 'date', text: r => fmtLocalDate(r.requestedDate) ?? '—', render: r => fmtLocalDate(r.requestedDate) ?? '—' },
    { field: 'itemName',      header: 'Item',        width: 220, text: r => r.itemName ?? '—', render: r => r.itemName ?? '—' },
    { field: 'specification', header: 'Description', width: 260, text: r => r.specification ?? '—', render: r => r.specification ?? '—' },
    { field: 'vendorName',    header: 'Vendor',      width: 150, text: r => r.vendorName ?? '—', render: r => r.vendorName ?? '—' },
    { field: 'orderQty',      header: 'OrderQty',    width: 100, type: 'number', align: 'right', text: r => String(r.orderQty), value: r => r.orderQty },
    { field: 'stockQty',      header: 'StockQty',    width: 100, type: 'number', align: 'right', text: r => String(r.stockQty), value: r => r.stockQty },
    { field: 'packUnit',      header: 'PackUnits',   width: 100, text: r => r.packUnit ?? '—', render: r => r.packUnit ?? '—' },
    { field: 'deliveryPriorityName', header: 'DeliveryPriority', width: 140, text: r => r.deliveryPriorityName ?? '—', render: r => r.deliveryPriorityName ?? '—' },
    { field: 'departmentName', header: 'Department', width: 140, text: r => r.departmentName ?? '—', render: r => r.departmentName ?? '—' },
    { field: 'requestedByName', header: 'RequestedBy', width: 150, text: r => r.requestedByName ?? '—', render: r => r.requestedByName ?? '—' },
    { field: 'statusLabel',   header: 'Status',      width: 130, type: 'status', text: r => r.statusLabel ?? '—', render: r => r.statusLabel ?? '—' },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Inventory Management</span><i className="ti ti-chevron-right" />
            <span>Tracking</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Item Requisition Tracking</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="icm-req-tracking" loadMs={loadMs} loading={loading} />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'item-requisition-tracking', ...gridExportMatrix(filtered, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search RQ#, item, vendor, requested by…"
              data-unsaved-ignore
            />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
          <span className="ica-note">{total.toLocaleString()} requisition(s) pending review</span>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<RequisitionTrackingRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              defaultPageSize={20}
              selectable
              selectedIds={selected}
              onSelectionChange={setSelected}
              emptyText="No requisitions are pending review."
            />
          )}
        </div>
      </div>
    </div>
  );
}
